import { Component, OnInit } from '@angular/core';
import { CvDatabaseReportModel } from '../shared/models/cv-database-report-model';
import { CvDatabaseReportFilterModel } from '../shared/models/cv-database-report-filter-model';
import { StatsAndReportsService } from '../shared/services/stats-and-reports.service';
import { ConfigurationService } from '@admin/shared/services/configuration.service';
import { CommonLookupItem } from '@admin/shared/models/common-lookup-item.model';
import { LookupService } from '@admin/shared/services/lookup.service';
import { CvDatabaseClicksReportModel } from '@admin/stats-and-reports/shared/models/cv-database-clicks-report-model';
import { CvDatabaseInvitesDetailModel } from '@admin/stats-and-reports/shared/models/cv-database-invites-detail-model';
import { CustomerJobseekerViewEventSourceEnum } from '../shared/models/customer-jobseeker-view-event-source-enum';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-cv-database-report',
  templateUrl: './cv-database-report.component.html',
  styleUrls: ['./cv-database-report.component.less'],
  standalone: false,
})
export class CvDatabaseReportComponent implements OnInit {
  reportData: CvDatabaseReportModel;
  clicksReport: CvDatabaseClicksReportModel;
  cvBoxClicksReport: CvDatabaseClicksReportModel;
  annClicksReport: CvDatabaseClicksReportModel;
  matchingClicksReport: CvDatabaseClicksReportModel;

  invitesDetailReport: CvDatabaseInvitesDetailModel;

  cols: string[];
  rows: any[];
  cvDatabaseViewRows: any[];
  cvBoxApplicationViewRows: any[];
  annApplicationViewRows: any[];
  matchingViewRows: any[];
  inviteDetailRows: any[];

  chartData: any;
  filter: CvDatabaseReportFilterModel;

  dateFilterMaxDays: number;
  specialDateFormat: string;
  providerWebsiteList: CommonLookupItem<number, string>[];
  filterErrorMsg: string;

  constructor(
    private reportService: StatsAndReportsService,
    private config: ConfigurationService,
    private lookupService: LookupService,
    private translateService: TranslateService
  ) {
    this.reportData = new CvDatabaseReportModel();
    this.cols = [];
    this.rows = [];
    this.cvDatabaseViewRows = [];
    this.cvBoxApplicationViewRows = [];
    this.annApplicationViewRows = [];
    this.matchingViewRows = [];
    this.inviteDetailRows = [];
    this.invitesDetailReport = null;
    this.chartData = null;
    this.filter = new CvDatabaseReportFilterModel();
    this.providerWebsiteList = [];
    this.filterErrorMsg = null;
  }

  onFilter() {
    if (
      this.filter.dates &&
      this.filter.dates.from &&
      this.filter.dates.to &&
      this.filter.dates.to >= this.filter.dates.from
    ) {
      const dateRangeInDays =
        (this.filter.dates.to.getTime() - this.filter.dates.from.getTime()) /
        24 /
        60 /
        60 /
        1000;
      if (dateRangeInDays > this.dateFilterMaxDays) {
        this.filterErrorMsg = this.translateService.instant(
          'message.CvDbReportInvalidFilterMessage'
        );
        this.chartData = null;
      } else {
        this.filterErrorMsg = null;
        this.reportService
          .getCvDatabaseReport(this.filter)
          .subscribe((response) => {
            this.reportData = response;
            this.cols = Object.keys(response.stats);
            this.initChartData();
          });
      }
    } else {
      this.filterErrorMsg = null;
      this.chartData = null;
    }
    this.onHideAllRows();

    this.clicksReport = null;
    this.cvBoxClicksReport = null;
    this.annClicksReport = null;
    this.matchingClicksReport = null;
    this.invitesDetailReport = null;
    this.inviteDetailRows = [];
  }

  onExpand(row) {
    if (row.field == 'totalClicks' && !this.clicksReport) {
      this.reportService
        .getCvDatabaseClicksReport(
          this.filter,
          CustomerJobseekerViewEventSourceEnum.CvDatabase
        )
        .subscribe((response) => {
          this.clicksReport = response;
          this.cvDatabaseViewRows = this.cvDatabaseViewRows.filter(
            (r) => !r.collapsible
          );
          this.addCustomerRows();
        });
    } else if (
      row.field == 'cvBoxApplicationViews' &&
      !this.cvBoxClicksReport
    ) {
      this.reportService
        .getCvDatabaseClicksReport(
          this.filter,
          CustomerJobseekerViewEventSourceEnum.CvBox
        )
        .subscribe((response) => {
          this.cvBoxClicksReport = response;
          this.cvBoxApplicationViewRows = this.cvBoxApplicationViewRows.filter(
            (r) => !r.collapsible
          );
          this.addCustomerCvBoxRows();
        });
    } else if (row.field == 'annApplicationViews' && !this.annClicksReport) {
      this.reportService
        .getCvDatabaseClicksReport(
          this.filter,
          CustomerJobseekerViewEventSourceEnum.Announcement
        )
        .subscribe((response) => {
          this.annClicksReport = response;
          this.annApplicationViewRows = this.annApplicationViewRows.filter(
            (r) => !r.collapsible
          );
          this.addCustomerannRows();
        });
    } else if (row.field == 'matchingViews' && !this.matchingClicksReport) {
      this.reportService
        .getCvDatabaseClicksReport(
          this.filter,
          CustomerJobseekerViewEventSourceEnum.Matching
        )
        .subscribe((response) => {
          this.matchingClicksReport = response;
          this.matchingViewRows = this.matchingViewRows.filter(
            (r) => !r.collapsible
          );
          this.addMatchingRows();
        });
    } else if (row.field == 'invitesTotal' && !this.invitesDetailReport) {
      this.reportService
        .getCvDatabaseInvitesDetail(this.filter)
        .subscribe((response) => {
          this.invitesDetailReport = response;
          this.inviteDetailRows = this.buildInviteDetailRows(response);
        });
    } else {
      this.rows
        .filter((r) => r.collapsible)
        .forEach((r) => (r.collapsed = true));
    }

    if (row.field == 'totalClicks') {
      this.rows[5].clicksCollapsed = true;
    } else if (row.field == 'cvBoxApplicationViews') {
      this.rows[6].clicksCollapsed = true;
    } else if (row.field == 'annApplicationViews') {
      this.rows[7].clicksCollapsed = true;
    } else if (row.field == 'matchingViews') {
      this.rows[8].clicksCollapsed = true;
    } else if (row.field == 'invitesTotal') {
      this.rows[9].clicksCollapsed = true;
    }
  }

  onHide(row) {
    if (row.field == 'totalClicks') {
      this.cvDatabaseViewRows
        .filter((r) => r.collapsible)
        .forEach((r) => (r.collapsed = false));
      this.rows[5].clicksCollapsed = false;
    } else if (row.field == 'cvBoxApplicationViews') {
      this.cvBoxApplicationViewRows
        .filter((r) => r.collapsible)
        .forEach((r) => (r.collapsed = false));
      this.rows[6].clicksCollapsed = false;
    } else if (row.field == 'annApplicationViews') {
      this.annApplicationViewRows
        .filter((r) => r.collapsible)
        .forEach((r) => (r.collapsed = false));
      this.rows[7].clicksCollapsed = false;
    } else if (row.field == 'matchingViews') {
      this.matchingViewRows
        .filter((r) => r.collapsible)
        .forEach((r) => (r.collapsed = false));
      this.rows[8].clicksCollapsed = false;
    } else if (row.field == 'invitesTotal') {
      this.rows[9].clicksCollapsed = false;
    }
  }

  private buildInviteDetailRows(report: CvDatabaseInvitesDetailModel): any[] {
    const names = new Set<string>();
    for (const date of Object.keys(report.details)) {
      for (const name of Object.keys(report.details[date])) {
        names.add(name);
      }
    }

    const statuses = [
      { field: 'interested', header: 'int', color: '#00FF00' },
      { field: 'interestedAndApplied', header: 'int & app', color: '#006400' },
      { field: 'notResponded', header: 'not resp', color: '#9E9E9E' },
      { field: 'notInterested', header: 'not int', color: '#C62828' },
      { field: 'total', header: 'Total', color: '#00008B' },
    ];

    const rows = [];
    names.forEach((customer) => {
      statuses.forEach((s, i) =>
        rows.push({
          customer: customer,
          field: s.field,
          header: s.header,
          color: s.color,
          firstOfCustomer: i === 0,
          rowspan: statuses.length,
        })
      );
    });
    return rows;
  }

  onHideAllRows() {
    this.rows
      .filter((r) => r.collapsible)
      .forEach((r) => (r.collapsed = false));
    this.rows
      .filter((r) => r.clicksCollapsed)
      .forEach((r) => (r.clicksCollapsed = false));
  }

  private addCustomerRows() {
    const names = new Set<string>();
    for (const date of Object.keys(this.clicksReport.clicks)) {
      const customers = this.clicksReport.clicks[date];
      for (const name of Object.keys(customers)) {
        names.add(name);
      }
    }
    names.forEach((customer) => {
      this.cvDatabaseViewRows.push({
        field: customer,
        header: customer,
        clickable: false,
        collapsible: true,
        collapsed: true,
        clicksCollapsed: false,
      });
    });
  }

  private addCustomerCvBoxRows() {
    const names = new Set<string>();
    for (const date of Object.keys(this.cvBoxClicksReport.clicks)) {
      const customers = this.cvBoxClicksReport.clicks[date];
      for (const name of Object.keys(customers)) {
        names.add(name);
      }
    }
    names.forEach((customer) => {
      this.cvBoxApplicationViewRows.push({
        field: customer,
        header: customer,
        clickable: false,
        collapsible: true,
        collapsed: true,
        clicksCollapsed: false,
      });
    });
  }

  private addCustomerannRows() {
    const names = new Set<string>();
    for (const date of Object.keys(this.annClicksReport.clicks)) {
      const customers = this.annClicksReport.clicks[date];
      for (const name of Object.keys(customers)) {
        names.add(name);
      }
    }
    names.forEach((customer) => {
      this.annApplicationViewRows.push({
        field: customer,
        header: customer,
        clickable: false,
        collapsible: true,
        collapsed: true,
        clicksCollapsed: false,
      });
    });
  }

  private addMatchingRows() {
    const names = new Set<string>();
    for (const date of Object.keys(this.matchingClicksReport.clicks)) {
      const customers = this.matchingClicksReport.clicks[date];
      for (const name of Object.keys(customers)) {
        names.add(name);
      }
    }
    names.forEach((customer) => {
      this.matchingViewRows.push({
        field: customer,
        header: customer,
        clickable: false,
        collapsible: true,
        collapsed: true,
        clicksCollapsed: false,
      });
    });
  }

  private initChartData() {
    const data = {
      labels: [],
      datasets: [
        {
          label: 'Ann. with apply',
          data: [],
          fill: false,
          borderColor: '#4bc0c0',
        },
        {
          label: 'Ann.Applications',
          data: [],
          fill: false,
          borderColor: '#565656',
        },
        {
          label: 'CvBox Applications',
          data: [],
          fill: false,
          borderColor: '#8A2BE2',
        },
        {
          label: 'Registrations',
          data: [],
          fill: false,
          borderColor: '#FFCE56',
        },
        {
          label: 'Basic/Complete',
          data: [],
          fill: false,
          borderColor: '#36A2EB',
        },
        {
          label: 'Database views',
          data: [],
          fill: false,
          borderColor: '#FF6384',
        },
        {
          label: 'Cv box application views',
          data: [],
          fill: false,
          borderColor: '#FF6384',
        },
        {
          label: 'Ann application views',
          data: [],
          fill: false,
          borderColor: '#FF6384',
        },
        {
          label: 'Matching views',
          data: [],
          fill: false,
          borderColor: '#FF6384',
        },
        {
          label: 'Invites (total)',
          data: [],
          fill: false,
          borderColor: '#00008B',
        },
        {
          label: 'Invites: not responded',
          data: [],
          fill: false,
          borderColor: '#9E9E9E',
        },
        {
          label: 'Invites: interested',
          data: [],
          fill: false,
          borderColor: '#00FF00',
        },
        {
          label: 'Invites: int. & applied',
          data: [],
          fill: false,
          borderColor: '#006400',
        },
        {
          label: 'Invites: not interested',
          data: [],
          fill: false,
          borderColor: '#C62828',
        },
        {
          label: 'Jobseeker Sign-ins',
          data: [],
          fill: false,
          borderColor: '#795548',
        },
      ],
    };

    for (let date in this.reportData.stats) {
      let stat = this.reportData.stats[date];

      data.labels.push(date);
      data.datasets[0].data.push(stat.announcementsWithApply);
      data.datasets[1].data.push(stat.applications);
      data.datasets[2].data.push(stat.cvBoxApplications);
      data.datasets[3].data.push(stat.registrations);
      data.datasets[4].data.push(stat.profileFills);
      data.datasets[5].data.push(stat.totalClicks);
      data.datasets[6].data.push(stat.cvBoxApplicationViews);
      data.datasets[7].data.push(stat.annApplicationViews);
      data.datasets[8].data.push(stat.matchingJobseekerViews);
      data.datasets[9].data.push(stat.invitesTotal);
      data.datasets[10].data.push(stat.invitesNotResponded);
      data.datasets[11].data.push(stat.invitesInterested);
      data.datasets[12].data.push(stat.invitesInterestedAndApplied);
      data.datasets[13].data.push(stat.invitesNotInterested);
      data.datasets[14].data.push(stat.signins);
    }

    this.chartData = data;
  }

  private getSpecialDateFormat(format: string) {
    return format.replace('MM', 'mm').replace('yyyy', 'yy');
  }

  ngOnInit() {
    const config = this.config.getCachedConfig();
    this.specialDateFormat = this.getSpecialDateFormat(config.dateFormat);
    this.dateFilterMaxDays = config.salesReportMaxDays;

    const lookupData = this.lookupService.getCachedLookupData();
    this.providerWebsiteList = lookupData.providerWebsiteList;

    this.rows = [
      {
        field: 'announcementsWithApply',
        header: 'Ann. with apply',
        clickable: false,
        collapsible: false,
        collapsed: false,
        clicksCollapsed: false,
      },
      {
        field: 'applications',
        header: 'Ann.Applications',
        clickable: false,
        collapsible: false,
        collapsed: false,
        clicksCollapsed: false,
      },
      {
        field: 'cvBoxApplications',
        header: 'CvBox Applications',
        clickable: false,
        collapsible: false,
        collapsed: false,
        clicksCollapsed: false,
      },
      {
        field: 'registrations',
        header: 'Registrations',
        clickable: false,
        collapsible: false,
        collapsed: false,
        clicksCollapsed: false,
      },
      {
        field: 'profileFills',
        header: 'Basic/Complete',
        clickable: false,
        collapsible: false,
        collapsed: false,
        clicksCollapsed: false,
      },
      {
        field: 'totalClicks',
        header: 'Database views',
        clickable: true,
        collapsible: false,
        collapsed: false,
        clicksCollapsed: false,
      },
      {
        field: 'cvBoxApplicationViews',
        header: 'Cv box application views',
        clickable: true,
        collapsible: false,
        collapsed: false,
        clicksCollapsed: false,
      },
      {
        field: 'annApplicationViews',
        header: 'Ann application views',
        clickable: true,
        collapsible: false,
        collapsed: false,
        clicksCollapsed: false,
      },
      {
        field: 'matchingViews',
        header: 'Matching views',
        clickable: true,
        collapsible: false,
        collapsed: false,
        clicksCollapsed: false,
      },
      {
        field: 'invitesTotal',
        header: 'Invites',
        clickable: true,
        collapsible: false,
        collapsed: false,
        clicksCollapsed: false,
      },
      {
        field: 'signins',
        header: 'Jobseeker Sign-ins',
        clickable: false,
        collapsible: false,
        collapsed: false,
        clicksCollapsed: false,
      },
    ];
  }
}
